import { useState, useEffect, useRef, useId } from 'react';
import {
  uploadBrandLogo,
  uploadBrandHeroImage,
  uploadBrandGlyphImage,
  getImageUrl,
  deleteReferenceImage,
} from '../../services/firebase';
import toast from 'react-hot-toast';

export interface BrandImageUploadProps {
  brandId: string;
  variant: 'logo' | 'hero' | 'glyph';
  /**
   * Current image value — a URL for logos and glyphs, a URL or storage path for
   * heroes.
   */
  imageUrl?: string;
  sourceUrl?: string;
  /** Only relevant for the hero variant. */
  showHeroToggle?: boolean;
  useHeroImage?: boolean;
  onImageUrlChange: (url: string) => void;
  /** Not used by the glyph variant, which has no source-URL field. */
  onSourceUrlChange?: (url: string) => void;
  onUseHeroImageChange?: (value: boolean) => void;
  disabled?: boolean;
}

/**
 * Reusable logo / hero / glyph image upload control extracted from BrandEditor.
 * Owns its own preview + uploading + file-input state; the parent owns the
 * image URL, source URL and (for heroes) the "use hero image" toggle.
 */
export function BrandImageUpload({
  brandId,
  variant,
  imageUrl = '',
  sourceUrl = '',
  showHeroToggle = false,
  useHeroImage = false,
  onImageUrlChange,
  onSourceUrlChange,
  onUseHeroImageChange,
  disabled = false,
}: BrandImageUploadProps) {
  const isHero = variant === 'hero';
  const isGlyph = variant === 'glyph';
  // Unique per-instance id so the "use hero image" checkbox/label pair never
  // collides when more than one BrandImageUpload is mounted (e.g. the portal
  // form and the super-admin BrandEditor both in the DOM).
  const heroToggleId = `${useId()}-useHeroImage`;
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // The last imageUrl we resolved/set ourselves. Lets the sync effect tell an
  // external change (a brand being loaded for editing) apart from our own edits.
  const lastSyncedUrl = useRef<string | undefined>(undefined);

  // Sync the preview when imageUrl changes from outside this component.
  useEffect(() => {
    if (imageUrl === lastSyncedUrl.current) return;
    lastSyncedUrl.current = imageUrl;
    if (!imageUrl) {
      setPreview(null);
      return;
    }
    if (isHero) {
      // Hero values may be storage paths that need resolving to a URL.
      setPreview(null);
      getImageUrl(imageUrl).then((url) => setPreview(url)).catch(() => {});
    } else {
      setPreview(imageUrl.startsWith('http') ? imageUrl : null);
    }
  }, [imageUrl, isHero]);

  const handlePaste = (value: string) => {
    lastSyncedUrl.current = value;
    setPreview(value.startsWith('http') ? value : null);
    onImageUrlChange(value);
  };

  const handleRemove = async () => {
    // Delete from Storage if it's a storage path (hero only).
    if (isHero && imageUrl && !imageUrl.startsWith('http')) {
      try { await deleteReferenceImage(imageUrl); } catch {}
    }
    lastSyncedUrl.current = '';
    setPreview(null);
    onImageUrlChange('');
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !brandId) return;
    setUploading(true);
    try {
      if (isHero) {
        const storagePath = await uploadBrandHeroImage(brandId, file, file.name);
        lastSyncedUrl.current = storagePath;
        onImageUrlChange(storagePath);
        const url = await getImageUrl(storagePath);
        setPreview(url);
        toast.success('Hero image uploaded');
      } else if (isGlyph) {
        const url = await uploadBrandGlyphImage(brandId, file, file.name);
        lastSyncedUrl.current = url;
        setPreview(url);
        onImageUrlChange(url);
        toast.success('Glyph image uploaded');
      } else {
        const url = await uploadBrandLogo(brandId, file, file.name);
        lastSyncedUrl.current = url;
        setPreview(url);
        onImageUrlChange(url);
        toast.success('Logo uploaded');
      }
    } catch (err) {
      toast.error(
        isHero
          ? 'Failed to upload image'
          : isGlyph
            ? 'Failed to upload glyph image'
            : 'Failed to upload logo',
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const noun = isHero ? 'Image' : isGlyph ? 'Glyph' : 'Logo';
  const uploadLabel = uploading
    ? 'Uploading...'
    : `${preview ? 'Change' : 'Upload'} ${noun}`;

  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-1'>
        {isHero ? 'Hero Image' : isGlyph ? 'Glyph Image (optional)' : 'Logo'}
      </label>
      {/* Preview */}
      {preview && (
        <div className='mb-2 relative inline-block'>
          <img
            src={preview}
            alt={`${noun} preview`}
            className={isHero
              ? 'h-32 rounded-lg object-cover'
              : 'h-16 rounded-lg object-contain bg-gray-50 border border-gray-200'}
          />
          <button
            type='button'
            onClick={handleRemove}
            className='absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-black/70'
          >
            ×
          </button>
        </div>
      )}
      {/* Upload / URL input */}
      <div className='flex items-center gap-2'>
        <input
          ref={fileRef}
          type='file'
          accept='image/*'
          className='hidden'
          onChange={handleFile}
        />
        <button
          type='button'
          onClick={() => fileRef.current?.click()}
          disabled={uploading || disabled}
          className='px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50'
        >
          {uploadLabel}
        </button>
        <span className='text-xs text-gray-400'>or</span>
        <input
          type='text'
          value={imageUrl}
          onChange={(e) => handlePaste(e.target.value)}
          placeholder='Paste URL...'
          disabled={disabled}
          className='flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
        />
      </div>
      {variant === 'logo' && (
        <p className='mt-1 text-xs text-gray-400'>
          External URLs are fetched and re-uploaded to our Storage on save.
        </p>
      )}
      {isGlyph && (
        <p className='mt-1 text-xs text-gray-400'>
          Uploads go to our Storage; pasted URLs are stored as-is. Not used by
          extraction yet — reserved for future vision workflows.
        </p>
      )}
      {isHero && showHeroToggle && (
        <div className='mt-2 flex items-center gap-2'>
          <input
            type='checkbox'
            id={heroToggleId}
            checked={useHeroImage}
            onChange={(e) => onUseHeroImageChange?.(e.target.checked)}
            disabled={disabled}
            className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          <label htmlFor={heroToggleId} className='text-sm text-gray-600'>
            Always show hero image (skip map even if address exists)
          </label>
        </div>
      )}
      {!isGlyph && (
        <div className='mt-3'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            {isHero ? 'Hero image source URL' : 'Logo source URL'}
          </label>
          <input
            type='url'
            value={sourceUrl}
            onChange={(e) => onSourceUrlChange?.(e.target.value)}
            placeholder='https://...'
            disabled={disabled}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          />
        </div>
      )}
    </div>
  );
}
